// === ONGEVALLEN SCREEN ===
// Ongevallenverzekering (DFD accident insurance) product screen.
//
// DELIBERATELY THIN, same config-driven model as DoorlopendeReisScreen: the product
// constants live in screens/ongevallen/fields.ts, the payload builder in calc.ts.
// Keep this file lean; new content goes into the config + shared components, not here.

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useWorkflow,
  useSharedAddress,
  useConfig,
  WorkflowPageLayout,
  getNextScreen,
  getPreviousScreen,
  getCurrentStepNumber,
  calculateTotalSteps,
  bffClient,
} from '@sluitstraat/core';
import { commonStyles } from '../../styles/commonStyles';
import { WorkflowNavButtons } from '../../components/WorkflowNavButtons';
import { AddressSection, Address } from '../../components/AddressSection';
import { PersonalInfoSection } from '../../components/PersonalInfoSection';
import { SelectField } from '../../components/SelectField';
import { RadioGroup } from '../../components/RadioGroup';
import { DebugPanel } from '../../components/DebugPanel';
import { CoverageOptionsCard } from '../../components/CoverageOptionsCard';
import { WithHelpSidebar } from '../../components/HelpCard';
import { shouldHideAddressSection } from '../../utils/addressSharing';
import { getCurrentLocale } from '../../utils/currency';
import { findUpstreamFamilyComposition, findSharedFamilyComposition, syncFamilyCompositionToOtherProducts } from '../../utils/familyComposition';
import { useSharedFamilyComposition } from '../../hooks/useSharedFamilyComposition';
import {
  buildOngevallenRequest,
  gebdatN8,
  normalizeOngevallenVwCodes,
  ONGEVALLEN_CALC_ENDPOINT,
  extractPremiums,
  extractEngineMessages,
  OngevallenPremiums,
  VpAddress,
} from './ongevallen/calc';
import {
  ONGEVALLEN_DEFAULTS,
  hiddenOngevallenFields,
  ongevallenMissingRequired,
  SelectOption,
  FAMILY_COMPOSITION_FIELD,
  DEATH_SUM_OPTIONS,
  INVALID_SUM_OPTIONS,
  invalidSumsForOverl,
  KIDS_INVALID_OPTIONS,
  OVERL_FIELD,
  INVALID_FIELD,
  KIDS_INVALID_FIELD,
  sumsForCombination,
  combinationForSums,
  kidsSumsForCombination,
  BERMIL_FIELD,
  BERMILP_FIELD,
  GSS_WITH_CHILDREN,
  GSS_WITH_PARTNER,
} from './ongevallen/fields';

const SCREEN_ID = 'ongevallen';

export const OngevallenScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getStepData, saveStepData, selectedProducts, relationData } = useWorkflow();
  const { address: sharedAddress } = useSharedAddress();
  const { config } = useConfig();
  const hiddenFields = hiddenOngevallenFields(config);

  const calcDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const calcController = useRef<AbortController | null>(null);

  const currentStep = getCurrentStepNumber(`/workflow/${SCREEN_ID}`, selectedProducts);
  const totalSteps = calculateTotalSteps(selectedProducts);

  const hideAddress = shouldHideAddressSection('ongevallen', selectedProducts, getStepData);

  const [values, setValues] = useState<Record<string, string | number>>(() => {
    const saved = getStepData(SCREEN_ID)?.values;
    const base = { ...ONGEVALLEN_DEFAULTS, ...(saved || {}) };
    if (!base.OD_GSS) {
      const shared = findSharedFamilyComposition(getStepData);
      if (shared) base.OD_GSS = shared;
    }
    // Records saved before the sums were separate fields only carry the combination
    // codes — expand them so the dropdowns show what was chosen.
    if (base.OD_OVERL == null || base.OD_INVALID == null) {
      const combi = sumsForCombination(base.OD_COMBINA);
      base.OD_OVERL = combi.overl;
      base.OD_INVALID = combi.invalid;
    }
    if (base.OD_VBBIKND == null) {
      base.OD_VBBIKND = kidsSumsForCombination(base.OD_COMBIKN).invalid;
    }
    return base;
  });

  const valuesRef = useRef(values);

  const [premiums, setPremiums] = useState<OngevallenPremiums | null>(() => {
    const stored = getStepData(SCREEN_ID)?.Contractdocument;
    return stored?.PP ? extractPremiums({ output: { Contractdocument: stored } }) : null;
  });
  const [isCalculating, setIsCalculating] = useState(false);
  const [calcInput, setCalcInput] = useState<any>(null);
  const [calcOutput, setCalcOutput] = useState<any>(null);
  const [calcErrors, setCalcErrors] = useState<string[]>([]);
  const [calcFailed, setCalcFailed] = useState(false);

  useEffect(() => {
    if (!selectedProducts?.ongevallen) {
      navigate('/workflow/product-selection', { replace: true });
      return;
    }
    const clientInfo = relationData?.clientInfo;
    if (!clientInfo || !clientInfo.email || !clientInfo.initials || !clientInfo.lastName) {
      navigate('/workflow/client-information', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    saveStepData(SCREEN_ID, { ...getStepData(SCREEN_ID), values });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const vpFromStep = (): VpAddress => {
    const vp = getStepData(SCREEN_ID)?.Relatiedocument?.VP || {};
    return {
      VP_PCODE: vp.VP_PCODE,
      VP_HUISNR: vp.VP_HUISNR,
      VP_TOEVOEG: vp.VP_TOEVOEG,
      VP_STRAAT: vp.VP_STRAAT,
      VP_PLAATS: vp.VP_PLAATS,
    };
  };

  const vpRef = useRef<VpAddress>(vpFromStep());

  const doCalculate = async () => {
    const vals = valuesRef.current;
    const vp = vpRef.current;
    if (!vp.VP_STRAAT || !vp.VP_PLAATS) return;
    if (ongevallenMissingRequired(vals, hiddenFields)) {
      setPremiums(null);
      setCalcErrors([]);
      setCalcFailed(false);
      return;
    }
    calcController.current?.abort();
    const ctrl = new AbortController();
    calcController.current = ctrl;
    const gebdat = gebdatN8(relationData?.clientInfo?.dateOfBirth);
    const lastName = relationData?.clientInfo?.lastName;
    const person: Record<string, string | number> = {
      ...(gebdat ? { VZ_GEBDAT: gebdat, VP_GEBDAT: gebdat } : {}),
      ...(lastName ? { VP_ANAAM: lastName } : {}),
    };
    const request = buildOngevallenRequest({ ...vals, ...person }, vp);
    setCalcInput(request);
    setIsCalculating(true);
    try {
      const res = await bffClient.request<any>('POST', ONGEVALLEN_CALC_ENDPOINT, request, ctrl.signal);
      setCalcOutput(res);
      const prem = extractPremiums(res);
      const errs = extractEngineMessages(res);
      const priced = (prem.display ?? 0) > 0;
      setPremiums(priced ? prem : null);
      setCalcErrors(errs);
      setCalcFailed(!priced && errs.length === 0);
      const contract = res?.output?.Contractdocument;
      if (contract && priced && errs.length === 0) {
        normalizeOngevallenVwCodes(contract);
        saveStepData(SCREEN_ID, {
          ...getStepData(SCREEN_ID),
          values: vals,
          Relatiedocument: { VP: vpRef.current },
          Contractdocument: contract,
        });
      }
    } catch (e: any) {
      if (ctrl.signal.aborted) return; // superseded by a newer calc
      setCalcOutput({ error: e instanceof Error ? e.message : 'Calculation failed' });
      setPremiums(null);
      setCalcErrors([]);
      setCalcFailed(true);
    } finally {
      if (calcController.current === ctrl) setIsCalculating(false);
    }
  };

  const recalc = () => {
    if (calcDebounce.current) clearTimeout(calcDebounce.current);
    calcDebounce.current = setTimeout(() => void doCalculate(), 400);
  };

  const setFields = (patch: Record<string, string | number>) => {
    const next = { ...valuesRef.current, ...patch };
    valuesRef.current = next;
    setValues(next);
    saveStepData(SCREEN_ID, { ...getStepData(SCREEN_ID), values: next });
    const gss = patch['OD_GSS'];
    if (typeof gss === 'string' && gss) {
      syncFamilyCompositionToOtherProducts(SCREEN_ID, gss, selectedProducts, getStepData, saveStepData);
    }
    recalc();
  };

  const setField = (key: string, value: string | number) => setFields({ [key]: value });

  const setOverl = (value: string) => {
    const overl = Number(value);
    const choices = invalidSumsForOverl(overl);
    const patch: Record<string, string | number> = { OD_OVERL: overl };
    if (choices.length > 0 && !choices.includes(Number(valuesRef.current['OD_INVALID']))) {
      patch.OD_INVALID = choices[0];
    }
    setFields(patch);
  };

  const resolveOptions = (opts: SelectOption[]) =>
    opts.map((o) => ({ value: String(o.value), label: o.labelKey ? t(o.labelKey, o.label) : o.label }));

  const sumFormat = new Intl.NumberFormat(getCurrentLocale(), { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const amountOptions = (sums: readonly number[]) =>
    sums.map((s) => ({ value: String(s), label: sumFormat.format(s) }));
  const overlOptions = amountOptions(DEATH_SUM_OPTIONS);
  const invalidChoices = invalidSumsForOverl(values['OD_OVERL']);
  const invalidOptions = amountOptions(invalidChoices.length > 0 ? invalidChoices : INVALID_SUM_OPTIONS);
  const invalidLocked = invalidChoices.length === 1;
  const kidsInvalidOptions = amountOptions(KIDS_INVALID_OPTIONS);

  const upstreamGss = findUpstreamFamilyComposition(getStepData, SCREEN_ID, selectedProducts);
  const hideFamilyComposition = !!upstreamGss;

  const { familyComposition, setFamilyComposition } = useSharedFamilyComposition();

  useEffect(() => {
    if (!familyComposition) {
      const found = findSharedFamilyComposition(getStepData);
      if (found) setFamilyComposition(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyComposition]);

  useEffect(() => {
    if (!familyComposition || valuesRef.current['OD_GSS'] === familyComposition) return;
    const next = { ...valuesRef.current, OD_GSS: familyComposition };
    valuesRef.current = next;
    setValues(next);
    saveStepData(SCREEN_ID, { ...getStepData(SCREEN_ID), values: next });
    recalc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyComposition]);

  useEffect(() => {
    if (upstreamGss && valuesRef.current['OD_GSS'] !== upstreamGss) {
      setField('OD_GSS', upstreamGss);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upstreamGss]);

  const initialAddress: Partial<Address> | undefined = (() => {
    const vp = getStepData(SCREEN_ID)?.Relatiedocument?.VP;
    if (!vp) return undefined;
    return {
      postcode: vp.VP_PCODE || '',
      houseNumber: vp.VP_HUISNR ? String(vp.VP_HUISNR) : '',
      addition: vp.VP_TOEVOEG || '',
      street: vp.VP_STRAAT || '',
      city: vp.VP_PLAATS || '',
    };
  })();

  const saveVp = (vp: Record<string, string>) => {
    vpRef.current = {
      VP_PCODE: vp.VP_PCODE,
      VP_HUISNR: vp.VP_HUISNR,
      VP_TOEVOEG: vp.VP_TOEVOEG,
      VP_STRAAT: vp.VP_STRAAT,
      VP_PLAATS: vp.VP_PLAATS,
    };
    saveStepData(SCREEN_ID, { ...getStepData(SCREEN_ID), Relatiedocument: { VP: vp } });
  };

  const handleAddressChange = (a: Address) => {
    saveVp({
      VP_PCODE: a.postcode,
      VP_HUISNR: a.houseNumber,
      VP_TOEVOEG: a.addition,
      VP_STRAAT: a.street,
      VP_PLAATS: a.city,
    });
    recalc();
  };

  useEffect(() => {
    if (!premiums) recalc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!sharedAddress?.BA_PCODE) return;
    const next = {
      VP_PCODE: sharedAddress.BA_PCODE,
      VP_HUISNR: String(sharedAddress.BA_HUISNR ?? ''),
      VP_TOEVOEG: sharedAddress.BA_TOEVOEG || '',
      VP_STRAAT: sharedAddress.BA_STRAAT || '',
      VP_PLAATS: sharedAddress.BA_PLAATS || '',
    };
    const cur = vpRef.current;
    if (cur.VP_PCODE === next.VP_PCODE
      && String(cur.VP_HUISNR ?? '') === next.VP_HUISNR
      && (cur.VP_TOEVOEG || '') === next.VP_TOEVOEG
      && (cur.VP_STRAAT || '') === next.VP_STRAAT
      && (cur.VP_PLAATS || '') === next.VP_PLAATS) return;
    saveVp(next);
    recalc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedAddress]);

  const handleBack = () => {
    const prevScreen = getPreviousScreen(`/workflow/${SCREEN_ID}`, selectedProducts);
    navigate(prevScreen || '/workflow/product-selection');
  };

  const handleNext = () => {
    const contract = normalizeOngevallenVwCodes(calcOutput?.output?.Contractdocument);
    saveStepData(SCREEN_ID, {
      ...getStepData(SCREEN_ID),
      values,
      ...(contract ? { Contractdocument: contract } : {}),
      completed: true,
    });
    const nextScreen = getNextScreen(`/workflow/${SCREEN_ID}`, selectedProducts);
    if (nextScreen) navigate(nextScreen);
  };

  const gss = familyComposition || String(values['OD_GSS'] ?? '');
  const hasChildren = (GSS_WITH_CHILDREN as readonly string[]).includes(gss);
  const hasPartner = (GSS_WITH_PARTNER as readonly string[]).includes(gss);

  const pairValid = !!combinationForSums(values['OD_OVERL'], values['OD_INVALID']);
  // AVP-style benefit bullets (coverageInfo.X.itemN); the sums follow the selection.
  const sumDescriptions = [
    t('screens.ongevallen.coverageInfo.accident.item1', { overl: sumFormat.format(Number(values['OD_OVERL'] ?? 10000)) }),
    t('screens.ongevallen.coverageInfo.accident.item2', { invalid: sumFormat.format(Number(values['OD_INVALID'] ?? 100000)) }),
    t('screens.ongevallen.coverageInfo.accident.item3'),
    t('screens.ongevallen.coverageInfo.accident.item4'),
  ];

  return (
    <WorkflowPageLayout currentStep={currentStep} totalSteps={totalSteps}>
      <main style={commonStyles.page}>
        <div style={commonStyles.container}>
          <WithHelpSidebar stackEarly>
            <header style={commonStyles.contentHeader}>
              <h1 style={commonStyles.title}>{t('products.ongevallen.name')}</h1>
            </header>

            <div style={commonStyles.formColumn}>
              {!hideAddress && <AddressSection value={initialAddress} onChange={handleAddressChange} />}

              <PersonalInfoSection
                value={gss}
                onChange={(v) => { setField('OD_GSS', v); setFamilyComposition(v); }}
                options={resolveOptions(FAMILY_COMPOSITION_FIELD.options)}
                hideFamilyField={hideFamilyComposition}
              >
                <div className="sl-pi-fields-row">
                  <SelectField
                    id="od-overl"
                    label={t(OVERL_FIELD.labelKey, OVERL_FIELD.fallbackLabel)}
                    value={String(values['OD_OVERL'] ?? '')}
                    onChange={setOverl}
                    options={overlOptions}
                    required
                  />
                  <SelectField
                    id="od-invalid"
                    label={t(INVALID_FIELD.labelKey, INVALID_FIELD.fallbackLabel)}
                    value={String(values['OD_INVALID'] ?? '')}
                    onChange={(v) => setField('OD_INVALID', Number(v))}
                    options={invalidOptions}
                    disabled={invalidLocked}
                    hidePlaceholder={invalidLocked}
                    required
                  />
                </div>
                {hasChildren && (
                  <div className="sl-pi-fields-row">
                    <SelectField
                      id="od-vbbiknd"
                      label={t(KIDS_INVALID_FIELD.labelKey, KIDS_INVALID_FIELD.fallbackLabel)}
                      value={String(values['OD_VBBIKND'] ?? '')}
                      onChange={(v) => setField('OD_VBBIKND', Number(v))}
                      options={kidsInvalidOptions}
                      required
                    />
                    <div />
                  </div>
                )}
                {calcErrors.map((e) => (
                  <p key={e} style={commonStyles.fieldErrorMessage}>{e}</p>
                ))}
                {(!hiddenFields.has(BERMIL_FIELD.key) || (hasPartner && !hiddenFields.has(BERMILP_FIELD.key))) && (
                <div className="sl-pi-fields-row">
                  {!hiddenFields.has(BERMIL_FIELD.key) && (
                  <div>
                    <label style={commonStyles.fieldLabel}>
                      {t(BERMIL_FIELD.labelKey, BERMIL_FIELD.fallbackLabel)}<span style={commonStyles.required}> *</span>
                    </label>
                    <RadioGroup
                      value={String(values['VZ_BERMIL'] ?? '')}
                      onSelect={(v) => setField('VZ_BERMIL', v)}
                      options={resolveOptions(BERMIL_FIELD.options)}
                    />
                  </div>
                  )}
                  {hasPartner && !hiddenFields.has(BERMILP_FIELD.key) && (
                    <div>
                      <label style={commonStyles.fieldLabel}>
                        {t(BERMILP_FIELD.labelKey, BERMILP_FIELD.fallbackLabel)}<span style={commonStyles.required}> *</span>
                      </label>
                      <RadioGroup
                        value={String(values['VZ_BERMILP'] ?? '')}
                        onSelect={(v) => setField('VZ_BERMILP', v)}
                        options={resolveOptions(BERMILP_FIELD.options)}
                      />
                    </div>
                  )}
                </div>
                )}
              </PersonalInfoSection>

              <CoverageOptionsCard
                title={t('screens.ongevallen.sections.coverageOptions', 'Dekking & premie')}
                totalLabel={t('common.total', 'Totaal')}
                total={premiums?.display ?? null}
                calculating={isCalculating}
                items={[
                  {
                    label: t('products.ongevallen.name', 'Ongevallenverzekering'),
                    descriptions: sumDescriptions,
                    amount: premiums?.display ?? null,
                  },
                ]}
              />
              {calcFailed && !isCalculating && (
                <p style={commonStyles.fieldErrorMessage}>
                  {t('common.premiumCalculationError', 'De premie kan op dit moment niet worden berekend. Probeer het later opnieuw of neem contact met ons op.')}
                </p>
              )}

              <DebugPanel
                input={calcInput}
                output={calcOutput}
                onRecalculate={() => recalc()}
                calculating={isCalculating}
              />
            </div>

            <WorkflowNavButtons onBack={handleBack} onNext={handleNext} nextDisabled={!pairValid || calcErrors.length > 0 || calcFailed} />
          </WithHelpSidebar>

        </div>
      </main>
    </WorkflowPageLayout>
  );
};

export default OngevallenScreen;
